/**
 * FetchContentLikedUsersUseCase
 *
 * Loads one page of users who liked a given piece of content (the owner-only
 * "users who liked this" sheet) and drops any row that touches a blocked
 * relationship with the current user.
 *
 * Flutter parity + web defence-in-depth:
 *   - lib/data/data_sources/users_data_source.dart:281-284 runs the blocked
 *     filter against `currentUser.usersBlockedMe` ONLY, keyed on the like's
 *     `userId`:
 *       if (currentUser.usersBlockedMe != null) {
 *         likes = likes.where((like) => !currentUser.usersBlockedMe!.contains(like.userId));
 *       }
 *   - On web we reuse the shared `shouldFilterOutBlockedUserUseCase`, which
 *     additionally checks `currentUser.blockedUsers` as defence-in-depth. This
 *     is the established web pattern — see
 *     `fetchExclusiveContentSubscribersPageUseCase.ts`, which filters its rows
 *     the same way ("a small in-memory filter against
 *     `currentUser.blockedUsers` / `usersBlockedMe`"). We deliberately do NOT
 *     introduce a second `usersBlockedMe`-only predicate.
 *   - Page size: AppConstants.subscriptionsPagination (= 9), byte-identical to
 *     SERVER_FETCH_CONSTANTS.DEFAULT_LIMIT on web. See
 *     content_users_liked_bottom_sheet.dart:89-116 (`getData()`).
 *
 * Errors from the service propagate — the hook layer logs via
 * appLogger.error.
 */

import type { PageCursor } from '@/data/types/json';

import { SERVER_FETCH_CONSTANTS } from '@/domain/constants/serverFetchConstants';
import type { ContentLikedUserEntity } from '@/domain/entities/contentReaction/contentLikedUserEntity';
import type { UserEntity } from '@/domain/entities/user/userEntity';
import {
  contentReactionsService,
  type ContentReactionsService,
} from '@/domain/services/contentReaction/contentReactionsService';
import { shouldFilterOutBlockedUserUseCase } from '@/domain/usecases/user/shouldFilterOutBlockedUserUseCase';

export interface FetchContentLikedUsersParams {
  mediaPath: string;
  currentUser: UserEntity | null;
  pageSize?: number;
  cursor?: PageCursor;
}

export interface FetchContentLikedUsersResult {
  likedUsers: ContentLikedUserEntity[];
  hasMore: boolean;
  cursor?: PageCursor;
}

export class FetchContentLikedUsersUseCase {
  private readonly reactionsService: ContentReactionsService;

  constructor(reactionsService?: ContentReactionsService) {
    this.reactionsService = reactionsService ?? contentReactionsService;
  }

  async execute(params: FetchContentLikedUsersParams): Promise<FetchContentLikedUsersResult> {
    const { mediaPath, currentUser, pageSize = SERVER_FETCH_CONSTANTS.DEFAULT_LIMIT, cursor } =
      params;

    const page = await this.reactionsService.fetchContentLikedUsers(mediaPath, pageSize, cursor);

    // The filter keys on the row's `userId`, matching Flutter
    // users_data_source.dart:283 (`currentUser.usersBlockedMe!.contains(like.userId)`).
    const likedUsers = page.likedUsers.filter(
      (row) => !shouldFilterOutBlockedUserUseCase.execute(row.userId, currentUser),
    );

    return {
      likedUsers,
      hasMore: page.hasMore,
      cursor: page.cursor,
    };
  }
}

export const fetchContentLikedUsersUseCase = new FetchContentLikedUsersUseCase();
